/**
 * Build the GitHub App install redirect for this app.
 *
 * When the workspace is already scoped to a git org, pass its account id so
 * GitHub preselects that account instead of showing the account picker. The
 * preselect is only a hint: the install webhook still rejects an installation
 * whose account does not match the stored scope, so this exists to stop people
 * picking the wrong org, not to enforce anything.
 *
 * @param returnTo Path to come back to after the install completes.
 * @param gitOrgId Provider account id of the scoped git org, if known.
 */
export const githubInstallUrl = (returnTo: string, gitOrgId?: string) => {
  const params = new URLSearchParams({ return_to: returnTo });
  if (gitOrgId) params.set('target_id', gitOrgId);
  return `/api/github/installation?${params.toString()}`;
};

/**
 * Does this `return_to` point back into the onboarding flow?
 *
 * `/api/github/installation` is shared: onboarding, Settings > Code Management,
 * the Documentation and Ask-Ellie onboardings, and Background Agents all send
 * users through it. The initiator stamps `flow=onboarding` on the callback URL
 * when this returns true, and the callback uses that to pick its fallback when
 * `return_to` is missing or expired — `/onboarding` for the onboarding flow, the
 * app root for everyone else. Misclassifying a caller sends it to the wrong
 * place, which is the bug the per-flow fallback exists to prevent.
 *
 * Two deliberate strictnesses:
 *   - whole path SEGMENTS, not a string prefix, so sibling routes such as
 *     `/onboarding-preview` are not swept in by `startsWith('/onboarding')`.
 *   - same ORIGIN only, so an attacker-supplied
 *     `https://evil.example/onboarding` is not classified as our own flow. The
 *     callback's open-redirect guard would refuse it anyway; this does not lean
 *     on that second line of defence.
 *
 * @param returnTo Raw `return_to` value — absolute or app-relative.
 * @param appBaseUrl Canonical public base URL, used to resolve and to compare origins.
 */
export const isOnboardingInstall = (
  returnTo: string,
  appBaseUrl: string
): boolean => {
  try {
    const parsed = new URL(returnTo, appBaseUrl);
    if (parsed.origin !== new URL(appBaseUrl).origin) return false;
    const { pathname } = parsed;
    return pathname === '/onboarding' || pathname.startsWith('/onboarding/');
  } catch {
    return false;
  }
};
